import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  ArrowLeft,
  Save,
  Car,
  Smartphone,
  CloudUpload,
  Link as LinkIcon,
  Loader2,
  Check,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { useIsMobile } from '../../../hooks/use-mobile';
import VehicleImageUpload from '../../../components/VehicleImageUpload';
import { cn } from '../../../lib/utils';

const FORM_ID = 'add-vehicle-form';

export default function Vehicles() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [imageMode, setImageMode] = useState('upload');
  const [uploadedImageData, setUploadedImageData] = useState('');
  const [uploadedImagePreview, setUploadedImagePreview] = useState('');
  const [uploadedImageName, setUploadedImageName] = useState('');
  const [isImageProcessing, setIsImageProcessing] = useState(false);
  const { post, get } = useApi();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      number: '',
      vehicle_name: '',
      device_id: '',
      display_image: '',
    },
  });

  const selectedDeviceId = watch('device_id');
  const displayImageUrl = watch('display_image');
  const activePreview = uploadedImagePreview || displayImageUrl;

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const [assignmentsResponse, vehiclesResponse] = await Promise.all([
        get('/customer-device-assignments?active=1&per_page=1000'),
        get('/customer/my-vehicles?per_page=1000&page=1'),
      ]);

      const assignments = Array.isArray(assignmentsResponse?.data)
        ? assignmentsResponse.data
        : Array.isArray(assignmentsResponse)
          ? assignmentsResponse
          : [];

      const existingVehicles = Array.isArray(vehiclesResponse?.data)
        ? vehiclesResponse.data
        : Array.isArray(vehiclesResponse)
          ? vehiclesResponse
          : [];

      const assignedDeviceIds = new Set(
        existingVehicles
          .map((vehicle) => vehicle?.device_id || vehicle?.deviceId)
          .filter(Boolean)
      );

      const deviceMap = new Map();
      assignments.forEach((assignment) => {
        const device = assignment?.device || {};
        const deviceType = device?.device_type || {};
        const deviceId = device?.device_id || device?._id || device?.id || assignment?.device_id;

        if (!deviceId || assignedDeviceIds.has(deviceId) || deviceMap.has(deviceId)) {
          return;
        }

        const deviceName =
          deviceType?.name ||
          device?.device_name ||
          device?.name ||
          'Device';
        const imei = device?.imei_number || device?.imeiNumber || device?.imei || '';

        deviceMap.set(deviceId, {
          id: deviceId,
          name: deviceName,
          imei,
          type: deviceType?.model_name || '',
        });
      });

      setDevices(Array.from(deviceMap.values()));
    } catch (error) {
      toast.error('Failed to fetch devices');
      setDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (!data.number) {
        toast.error('Please enter vehicle number');
        setIsLoading(false);
        return;
      }
      if (!data.vehicle_name) {
        toast.error('Please enter vehicle name');
        setIsLoading(false);
        return;
      }
      if (!data.device_id) {
        toast.error('Please select a device');
        setIsLoading(false);
        return;
      }

      let resolvedDisplayImage = '';

      if (imageMode === 'upload') {
        resolvedDisplayImage = uploadedImageData || '';
      } else {
        const trimmedUrl = (data.display_image || '').trim();
        if (trimmedUrl && !/^https?:\/\/.+/i.test(trimmedUrl)) {
          const message = 'Please enter a valid image URL (must start with http or https)';
          toast.error(message);
          setError('display_image', { type: 'pattern', message });
          setIsLoading(false);
          return;
        }
        resolvedDisplayImage = trimmedUrl;
        clearErrors('display_image');
      }

      const payload = {
        number: data.number,
        vehicle_name: data.vehicle_name,
        device_id: data.device_id,
        display_image: resolvedDisplayImage,
      };

      await post('/customer/my-vehicles', payload);

      await fetchDevices();
      reset();
      setUploadedImageData('');
      setUploadedImagePreview('');
      setUploadedImageName('');
      setImageMode('upload');

      toast.success('Vehicle created successfully!');
      navigate('/customer/vehicles');
    } catch (error) {
      let errorMessage = 'An error occurred while creating the vehicle. Please try again.';

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const switchImageMode = (mode) => {
    setImageMode(mode);
    clearErrors('display_image');
    if (mode === 'upload') {
      setValue('display_image', uploadedImageData || '');
    } else {
      setUploadedImageData('');
      setUploadedImagePreview('');
      setUploadedImageName('');
      setValue('display_image', '');
    }
  };

  const renderDevicePicker = () => {
    if (isLoadingDevices) {
      return (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-muted-foreground/25 bg-muted/20 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading devices...
        </div>
      );
    }

    if (devices.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-muted-foreground/25 bg-muted/20 px-4 py-8 text-center">
          <Smartphone className="mx-auto mb-2 h-8 w-8 text-muted-foreground/70" />
          <p className="text-sm font-medium text-foreground">No devices available</p>
          <p className="mt-1 text-xs text-muted-foreground">
            All assigned devices are already linked to a vehicle.
          </p>
        </div>
      );
    }

    if (isMobile) {
      return (
        <div className="space-y-2">
          {devices.map((device) => {
            const isSelected = selectedDeviceId === device.id;
            return (
              <button
                key={device.id}
                type="button"
                onClick={() => {
                  setValue('device_id', device.id, { shouldValidate: true });
                  clearErrors('device_id');
                }}
                className={cn(
                  'w-full rounded-xl border p-3.5 text-left transition-all touch-manipulation',
                  isSelected
                    ? 'border-primary/50 bg-primary/5 shadow-sm ring-2 ring-primary/15'
                    : 'border-muted-foreground/15 bg-muted/30 active:bg-muted/50'
                )}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                      isSelected ? 'bg-primary/15 text-primary' : 'bg-background text-muted-foreground'
                    )}
                  >
                    <Smartphone className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">{device.name}</p>
                    {device.type ? (
                      <p className="text-xs text-muted-foreground">{device.type}</p>
                    ) : null}
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                      {device.imei ? `IMEI ${device.imei}` : 'IMEI not available'}
                    </p>
                  </div>
                  {isSelected ? (
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <Select
        onValueChange={(value) => setValue('device_id', value)}
        value={selectedDeviceId}
      >
        <SelectTrigger className="h-11 border-muted-foreground/20 bg-muted/30" id="device_id">
          <SelectValue placeholder={isLoadingDevices ? 'Loading devices...' : 'Select a device'} />
        </SelectTrigger>
        <SelectContent className="z-[9999] border-muted-foreground/20">
          {devices.map((device) => {
            const labelParts = [
              device.name,
              device.type,
              device.imei ? `IMEI: ${device.imei}` : null,
            ].filter(Boolean);

            return (
              <SelectItem key={device.id} value={device.id}>
                <span className="font-medium">{labelParts.join(' - ')}</span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    );
  };

  return (
    <div
      className={cn(
        'w-full min-w-0 bg-background text-foreground',
        'pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] sm:pb-6'
      )}
    >
      {/* Mobile header */}
      <div className="mb-4 flex items-center gap-2 sm:hidden">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-lg"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Vehicles
          </p>
          <h1 className="truncate text-base font-semibold text-foreground">Add new vehicle</h1>
        </div>
      </div>

      {/* Desktop header */}
      <div className="mb-6 hidden sm:block">
        <h1 className="text-2xl font-bold tracking-tight">Add New Vehicle</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Register a vehicle and link it to your GPS device.
        </p>
      </div>

      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} className="mx-auto w-full max-w-2xl space-y-4 sm:space-y-5">
        {/* Vehicle details */}
        <section className="rounded-2xl border border-muted-foreground/15 bg-card p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Car className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground sm:text-base">Vehicle details</h2>
              <p className="text-xs text-muted-foreground">Required information</p>
            </div>
          </div>

          <div className="space-y-3.5">
            <div className="space-y-1.5">
              <Label htmlFor="number" className="text-xs font-medium text-muted-foreground">
                Vehicle number *
              </Label>
              <Input
                id="number"
                {...register('number', { required: 'Vehicle number is required' })}
                placeholder="e.g. TS09EK3227"
                className="h-11 border-muted-foreground/20 bg-muted/30 text-base sm:text-sm"
                autoComplete="off"
              />
              {errors.number ? (
                <p className="text-xs text-destructive">{errors.number.message}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="vehicle_name" className="text-xs font-medium text-muted-foreground">
                Vehicle name *
              </Label>
              <Input
                id="vehicle_name"
                {...register('vehicle_name', { required: 'Vehicle name is required' })}
                placeholder="e.g. Hero Duet"
                className="h-11 border-muted-foreground/20 bg-muted/30 text-base sm:text-sm"
                autoComplete="off"
              />
              {errors.vehicle_name ? (
                <p className="text-xs text-destructive">{errors.vehicle_name.message}</p>
              ) : null}
            </div>
          </div>
        </section>

        {/* Vehicle photo */}
        <section className="rounded-2xl border border-muted-foreground/15 bg-card p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground sm:text-base">Vehicle photo</h2>
              <p className="text-xs text-muted-foreground">Optional — helps identify your vehicle</p>
            </div>
          </div>

          {activePreview && isMobile ? (
            <div className="mb-4 overflow-hidden rounded-xl border border-muted-foreground/15 bg-muted/20">
              <img
                src={activePreview}
                alt="Vehicle preview"
                className="aspect-[16/10] w-full object-cover"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src =
                    'https://via.placeholder.com/400x240.png?text=Preview+Unavailable';
                }}
              />
            </div>
          ) : null}

          <div className="mb-3 grid grid-cols-2 gap-1 rounded-xl border border-muted-foreground/15 bg-muted/30 p-1">
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium transition touch-manipulation sm:text-sm',
                imageMode === 'upload'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground'
              )}
              onClick={() => switchImageMode('upload')}
            >
              <CloudUpload className="h-4 w-4 shrink-0" />
              Upload
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium transition touch-manipulation sm:text-sm',
                imageMode === 'url'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground'
              )}
              onClick={() => switchImageMode('url')}
            >
              <LinkIcon className="h-4 w-4 shrink-0" />
              Image link
            </button>
          </div>

          {imageMode === 'upload' ? (
            <VehicleImageUpload
              inputId="display_image_upload"
              previewUrl={isMobile ? '' : uploadedImagePreview}
              fileName={uploadedImageName}
              disabled={isLoading}
              isProcessing={isImageProcessing}
              onProcessingChange={setIsImageProcessing}
              onImageReady={({ dataUrl, previewUrl, fileName }) => {
                setUploadedImageData(dataUrl);
                setUploadedImagePreview(previewUrl);
                setUploadedImageName(fileName);
                setValue('display_image', dataUrl);
                clearErrors('display_image');
              }}
              onClear={() => {
                setUploadedImageData('');
                setUploadedImagePreview('');
                setUploadedImageName('');
                setValue('display_image', '');
              }}
            />
          ) : (
            <div className="space-y-2">
              <Input
                id="display_image_url"
                {...register('display_image')}
                placeholder="https://example.com/vehicle.jpg"
                className="h-11 border-muted-foreground/20 bg-muted/30 text-base sm:text-sm"
                inputMode="url"
              />
              <p className="text-xs text-muted-foreground">
                Paste a direct link to an image hosted online.
              </p>
              {!isMobile && activePreview ? (
                <div className="overflow-hidden rounded-xl border border-muted-foreground/15 bg-muted/20 p-2">
                  <img
                    src={activePreview}
                    alt="Vehicle preview"
                    className="max-h-44 w-full rounded-lg object-contain"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src =
                        'https://via.placeholder.com/400x240.png?text=Preview+Unavailable';
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}

          {errors.display_image ? (
            <p className="mt-2 text-xs text-destructive">{errors.display_image.message}</p>
          ) : null}
        </section>

        {/* Device */}
        <section className="rounded-2xl border border-muted-foreground/15 bg-card p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Smartphone className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground sm:text-base">GPS device *</h2>
              <p className="text-xs text-muted-foreground">Select the tracker for this vehicle</p>
            </div>
          </div>

          {renderDevicePicker()}

          <input
            type="hidden"
            {...register('device_id', { required: 'Device selection is required' })}
          />
          {errors.device_id ? (
            <p className="mt-2 text-xs text-destructive">{errors.device_id.message}</p>
          ) : null}
        </section>

        {/* Desktop actions */}
        <div className="hidden items-center justify-end gap-3 sm:flex">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading || isImageProcessing} className="min-w-[140px]">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Create vehicle
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Mobile sticky submit */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-muted-foreground/15 bg-background/95 backdrop-blur-md sm:hidden safe-bottom">
        <div className="grid grid-cols-[auto_1fr] gap-2 px-3 py-3 safe-x">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-11 w-11 shrink-0 rounded-xl"
            onClick={() => navigate(-1)}
            aria-label="Cancel"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={isLoading || isImageProcessing}
            className="h-11 rounded-xl text-sm font-semibold"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Create vehicle
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
